/**
 * What is pending on this material and who owns it, in order. One line
 * says the next thing; the row of steps shows where the material stands
 * from the read to in use. Clicking a step opens the section it lives in.
 */
import React from 'react';

const NODE = {
  done: 'bg-slate-900 text-white border-slate-900',
  now: 'bg-orange-600 text-white border-orange-600 ring-4 ring-orange-100',
  waiting: 'bg-amber-50 text-amber-800 border-amber-300',
  later: 'bg-white text-slate-400 border-slate-200',
  skip: 'bg-white text-slate-300 border-slate-200 border-dashed',
};
const WORD = { done: 'Done', now: 'Your turn', waiting: 'Waiting', later: 'Later', skip: 'Not needed' };
/** A label inside a sentence: "Confirm the facts" becomes "confirm the facts"; "HPMS" stays. */
export const inSentence = (label) => String(label || '').replace(/^[A-Z](?=[a-z])/, (ch) => ch.toLowerCase());

export default function NextSteps({ steps, headline, next, onGo }) {
  if (!steps?.length) return null;
  const n = next || null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] font-bold tracking-wide text-orange-700 uppercase">{n?.state === 'now' ? 'Next, yours' : n?.state === 'waiting' ? `Waiting on ${n.owner}` : 'Where it stands'}</div>
          <div className="text-sm text-slate-900 mt-0.5">{headline}</div>
        </div>
        {n && n.state === 'now' && <button type="button" onClick={() => onGo && onGo(n)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 shadow-sm">Go to {inSentence(n.label)}</button>}
      </div>
      <div className="mt-4 flex items-start overflow-x-auto pb-1 -mx-1">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <button type="button" onClick={() => onGo && onGo(s)} title={`${s.label}: ${s.detail} (${s.owner})`} className="group flex flex-col items-center text-center shrink-0 px-1" style={{ width: 118 }}>
              <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold transition ${NODE[s.state]}`}>
                {s.state === 'done' ? <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 10.5l3.2 3L15 7" strokeLinecap="round" strokeLinejoin="round" /></svg> : s.state === 'skip' ? '–' : i + 1}
              </span>
              <span className={`mt-1.5 text-xs font-semibold leading-tight ${s.state === 'now' ? 'text-orange-700' : s.state === 'skip' ? 'text-slate-400' : 'text-slate-800'}`}>{s.label}</span>
              <span className={`text-[10px] leading-tight mt-0.5 ${s.state === 'now' ? 'text-orange-700' : s.state === 'waiting' ? 'text-amber-700' : 'text-slate-400'}`}>{WORD[s.state]}</span>
              <span className="text-[10px] text-slate-500 leading-tight mt-0.5 line-clamp-2">{s.detail}</span>
            </button>
            {i < steps.length - 1 && <span className={`mt-4 h-0.5 shrink-0 ${['done', 'skip'].includes(s.state) ? 'bg-slate-900' : 'bg-slate-200'}`} style={{ width: 14, marginLeft: -7, marginRight: -7 }} />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
